import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Search,
  ShieldCheck,
  Sparkles,
  Ban,
  Check,
  Filter,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { adminApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GlassCard, PageLoading } from '../components/shared';
import { toast } from 'sonner';

const PROVENANCE_OPTIONS = [
  ['', 'Toutes'],
  ['editorial', 'Éditorial'],
  ['rule_quote_detection', 'Détection de citation'],
  ['derived_from_tafsir', 'Dérivé via tafsir'],
  ['derived_from_sanad', 'Dérivé via sanad'],
  ['derived_from_event', 'Dérivé via événement'],
  ['imported', 'Importé'],
  ['inferred', 'Inféré'],
];

const RELATION_OPTIONS = [
  ['', 'Toutes'],
  ['quotes', 'Citation'],
  ['references', 'Référence'],
  ['explains', 'Explique'],
  ['supports_interpretation', 'Appuie interp.'],
  ['linked_by_tafsir', 'Via tafsir'],
  ['event_context', 'Contexte hist.'],
  ['mentions', 'Mentionne'],
  ['narrates_about', 'Narre à propos'],
  ['related_to', 'En lien'],
];

const CONFIDENCE_TONE = {
  high: 'text-emerald-300 bg-emerald-900/30',
  medium: 'text-amber-300 bg-amber-900/30',
  low: 'text-gray-400 bg-gray-800/40',
};

const StatPill = ({ label, value, tone }) => (
  <div className={`px-3 py-2 rounded-lg border border-border bg-surface-elevated/60 ${tone || ''}`}>
    <p className="text-[10px] uppercase tracking-wider text-text-muted">{label}</p>
    <p className="text-lg font-serif text-text-primary">{value.toLocaleString('fr-FR')}</p>
  </div>
);

const LinkRow = ({ link, onPatch, onDelete }) => {
  const [notes, setNotes] = useState(link.review_notes || '');
  const [busy, setBusy] = useState(false);

  const doAction = async (patch) => {
    setBusy(true);
    try {
      await onPatch(link.id, patch);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="p-4 border border-border rounded-lg bg-surface-elevated/40"
      data-testid={`admin-link-row-${link.id}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${CONFIDENCE_TONE[link.confidence] || CONFIDENCE_TONE.low}`}>
          {link.confidence}
        </span>
        <span className="text-xs text-text-muted">{link.relation_type}</span>
        <span className="text-xs text-text-muted">· {link.provenance}</span>
        {link.reviewed && (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
            <ShieldCheck className="w-3 h-3" /> vérifié
          </span>
        )}
        {link.disabled && (
          <span className="inline-flex items-center gap-1 text-[10px] text-red-400">
            <Ban className="w-3 h-3" /> désactivé
          </span>
        )}
        {!link.reviewed && !link.disabled && (
          <span className="inline-flex items-center gap-1 text-[10px] text-text-muted">
            <Sparkles className="w-3 h-3" /> non vérifié
          </span>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Source</p>
          <p className="font-mono text-xs text-text-primary">
            {link.source_type} · {link.source_id}
          </p>
          {link.source_ref && Object.keys(link.source_ref).length > 0 && (
            <p className="text-xs text-text-muted mt-1">
              {[link.source_ref.collection_slug, link.source_ref.book_number && `Livre ${link.source_ref.book_number}`, link.source_ref.id_in_book && `#${link.source_ref.id_in_book}`]
                .filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Cible</p>
          <p className="font-mono text-xs text-text-primary">
            {link.target_type} · {link.target_id}
          </p>
          {link.target_ref && (
            <p className="text-xs text-text-muted mt-1">
              {link.target_ref.title_fr || link.target_ref.title_en || link.target_ref.name_fr || link.target_ref.name_en || link.target_ref.source_name || link.target_ref.snippet || ''}
            </p>
          )}
        </div>
      </div>
      {link.evidence && (
        <p className="text-xs text-text-secondary italic mt-2">{link.evidence}</p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Note de révision (optionnel)"
          className="h-8 text-xs flex-1 min-w-[180px]"
          data-testid={`admin-link-notes-${link.id}`}
        />
        {!link.reviewed && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => doAction({ reviewed: true, review_notes: notes || undefined })}
            data-testid={`admin-link-approve-${link.id}`}
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Vérifier
          </Button>
        )}
        {link.reviewed && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => doAction({ reviewed: false })}
            data-testid={`admin-link-unapprove-${link.id}`}
          >
            Annuler vérif.
          </Button>
        )}
        {!link.disabled ? (
          <Button
            size="sm"
            variant="outline"
            className="text-red-300 border-red-900/40"
            disabled={busy}
            onClick={() => doAction({ disabled: true, review_notes: notes || undefined })}
            data-testid={`admin-link-disable-${link.id}`}
          >
            <Ban className="w-3.5 h-3.5 mr-1" /> Désactiver
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => doAction({ disabled: false })}
          >
            Réactiver
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="text-text-muted hover:text-red-400"
          disabled={busy}
          onClick={() => {
            if (window.confirm('Supprimer définitivement ce lien ?')) {
              onDelete(link.id);
            }
          }}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

const AdminLinks = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    provenance: '',
    relation: '',
    confidence: '',
    reviewed: '',
    search: '',
  });
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const loadStats = useCallback(async () => {
    try {
      const r = await adminApi.links.stats();
      setStats(r.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, skip };
      if (filters.provenance) params.provenance = filters.provenance;
      if (filters.relation) params.relation = filters.relation;
      if (filters.confidence) params.confidence = filters.confidence;
      if (filters.reviewed === 'yes') params.reviewed = true;
      if (filters.reviewed === 'no') params.reviewed = false;
      if (filters.search) params.search = filters.search;
      const r = await adminApi.links.list(params);
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error('Chargement échoué');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, skip]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(); }, [loadList]);

  const patchLink = async (id, patch) => {
    try {
      await adminApi.links.patch(id, patch);
      toast.success('Lien mis à jour');
      await Promise.all([loadList(), loadStats()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const deleteLink = async (id) => {
    try {
      await adminApi.links.remove(id);
      toast.success('Lien supprimé');
      await Promise.all([loadList(), loadStats()]);
    } catch (e) {
      toast.error('Échec');
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-text-muted">Accès administrateur requis.</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-links-page">
      <div className="max-w-7xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Dashboard admin
        </Link>
        <h1 className="text-3xl font-serif text-text-primary mb-2">Graphe sémantique — revue</h1>
        <p className="text-sm text-text-muted mb-6">
          Valide, annote ou désactive les liens sémantiques générés automatiquement ou importés.
        </p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-links-stats">
            <StatPill label="Total" value={stats.total} />
            <StatPill label="Vérifiés" value={stats.reviewed} tone="text-emerald-400" />
            <StatPill label="Non vérifiés" value={stats.unreviewed} tone="text-amber-400" />
            <StatPill label="Désactivés" value={stats.disabled} tone="text-red-400" />
          </div>
        )}

        <GlassCard className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Filtres</span>
            <Button size="sm" variant="ghost" onClick={() => { setFilters({ provenance:'', relation:'', confidence:'', reviewed:'', search:'' }); setSkip(0); }}>
              <RefreshCw className="w-3 h-3 mr-1" /> reset
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <select
              value={filters.provenance}
              onChange={(e) => { setFilters({ ...filters, provenance: e.target.value }); setSkip(0); }}
              className="h-9 px-2 rounded-md bg-surface border border-border text-sm"
              data-testid="admin-links-filter-provenance"
            >
              {PROVENANCE_OPTIONS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
            <select
              value={filters.relation}
              onChange={(e) => { setFilters({ ...filters, relation: e.target.value }); setSkip(0); }}
              className="h-9 px-2 rounded-md bg-surface border border-border text-sm"
              data-testid="admin-links-filter-relation"
            >
              {RELATION_OPTIONS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
            </select>
            <select
              value={filters.confidence}
              onChange={(e) => { setFilters({ ...filters, confidence: e.target.value }); setSkip(0); }}
              className="h-9 px-2 rounded-md bg-surface border border-border text-sm"
            >
              <option value="">Confiance : toutes</option>
              <option value="high">Haute</option>
              <option value="medium">Moyenne</option>
              <option value="low">Basse</option>
            </select>
            <select
              value={filters.reviewed}
              onChange={(e) => { setFilters({ ...filters, reviewed: e.target.value }); setSkip(0); }}
              className="h-9 px-2 rounded-md bg-surface border border-border text-sm"
            >
              <option value="">Statut : tous</option>
              <option value="yes">Vérifiés</option>
              <option value="no">Non vérifiés</option>
            </select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
              <Input
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); loadList(); } }}
                placeholder="Rechercher source/cible/evidence"
                className="pl-7 h-9 text-sm"
                data-testid="admin-links-filter-search"
              />
            </div>
          </div>
        </GlassCard>

        {loading ? <PageLoading /> : (
          <>
            <p className="text-xs text-text-muted mb-3">
              {total.toLocaleString('fr-FR')} liens · Page {currentPage} / {totalPages}
            </p>
            <div className="space-y-3">
              {items.map((lk) => (
                <LinkRow key={lk.id} link={lk} onPatch={patchLink} onDelete={deleteLink} />
              ))}
              {items.length === 0 && (
                <p className="text-center text-text-muted py-12">Aucun lien ne correspond.</p>
              )}
            </div>
            <div className="flex items-center justify-between mt-6">
              <Button size="sm" variant="outline" disabled={skip === 0}
                      onClick={() => setSkip(Math.max(0, skip - limit))}>
                Précédent
              </Button>
              <Button size="sm" variant="outline" disabled={skip + limit >= total}
                      onClick={() => setSkip(skip + limit)}>
                Suivant
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminLinks;
